/**
 * BYOC Bin QR Label Generator
 *
 * Generates printable QR code labels for BYOC (Bring Your Own Container) bins.
 * Each label includes:
 * - QR code with bin info
 * - Bin name
 * - Product name
 * - Price per pound
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStoreAuth } from '../../contexts/StoreAuthContext';
import StoreHeader from '../../components/Layout/StoreHeader';

interface BinLabel {
  id: number;
  name: string;
  formula_code: string | null;
  formula_name: string;
  price_per_lb: number | null;
  current_lbs: number;
  capacity_lbs: number;
  qr_image: string;
  qr_string: string;
}

const API_BASE = '/api';

const BYOCLabels: React.FC = () => {
  const { isAuthenticated } = useStoreAuth();
  const navigate = useNavigate();

  const [bins, setBins] = useState<BinLabel[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBins, setSelectedBins] = useState<Set<number>>(new Set());
  const [labelSize, setLabelSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [printMode, setPrintMode] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/store/login');
      return;
    }
    loadBins();
  }, [isAuthenticated, navigate]);

  const loadBins = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/qr/bins/`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to load bins');
      const data = await response.json();
      setBins(data.bins);
      // Select all by default
      setSelectedBins(new Set(data.bins.map((b: BinLabel) => b.id)));
    } catch (err) {
      console.error('Failed to load bins:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleBin = (binId: number) => {
    setSelectedBins(prev => {
      const next = new Set(prev);
      if (next.has(binId)) {
        next.delete(binId);
      } else {
        next.add(binId);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedBins.size === bins.length) {
      setSelectedBins(new Set());
    } else {
      setSelectedBins(new Set(bins.map(b => b.id)));
    }
  };

  const handlePrint = () => {
    setPrintMode(true);
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrintMode(false), 500);
    }, 100);
  };

  const selectedBinsList = bins.filter(b => selectedBins.has(b.id));

  const labelSizes = {
    small: { width: '150px', qrSize: '100px', fontSize: '0.7rem' },
    medium: { width: '200px', qrSize: '140px', fontSize: '0.85rem' },
    large: { width: '280px', qrSize: '200px', fontSize: '1rem' },
  };

  const currentSize = labelSizes[labelSize];

  if (!isAuthenticated) return null;

  return (
    <div style={printMode ? styles.printPage : styles.page}>
      {!printMode && <StoreHeader />}

      <main style={printMode ? styles.printMain : styles.main}>
        {!printMode && (
          <>
            <div style={styles.header}>
              <h1 style={styles.title}>BYOC Bin Labels</h1>
              <button
                style={styles.backBtn}
                onClick={() => navigate('/store/admin')}
              >
                Back to Admin
              </button>
            </div>

            <div style={styles.controls}>
              <div style={styles.controlGroup}>
                <label style={styles.label}>Label Size:</label>
                <select
                  value={labelSize}
                  onChange={e => setLabelSize(e.target.value as 'small' | 'medium' | 'large')}
                  style={styles.select}
                >
                  <option value="small">Small (2x2")</option>
                  <option value="medium">Medium (2.5x2.5")</option>
                  <option value="large">Large (3x3")</option>
                </select>
              </div>

              <button style={styles.selectAllBtn} onClick={toggleAll}>
                {selectedBins.size === bins.length ? 'Deselect All' : 'Select All'}
              </button>

              <button
                style={styles.printBtn}
                onClick={handlePrint}
                disabled={selectedBins.size === 0}
              >
                Print Labels ({selectedBins.size})
              </button>
            </div>

            {loading ? (
              <p style={styles.loading}>Loading bins...</p>
            ) : (
              <div style={styles.binList}>
                {bins.map(bin => (
                  <div
                    key={bin.id}
                    style={{
                      ...styles.binItem,
                      backgroundColor: selectedBins.has(bin.id) ? '#e8f5e9' : '#fff',
                      borderColor: selectedBins.has(bin.id) ? '#1a4d1a' : '#ddd',
                    }}
                    onClick={() => toggleBin(bin.id)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedBins.has(bin.id)}
                      onChange={() => toggleBin(bin.id)}
                      style={styles.checkbox}
                    />
                    <div style={styles.binInfo}>
                      <strong>{bin.name}</strong>
                      <span style={styles.binDetail}>
                        {bin.formula_name}
                        {bin.price_per_lb && ` - $${bin.price_per_lb.toFixed(2)}/lb`}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Print Preview / Printable Labels */}
        <div style={printMode ? styles.labelGridPrint : styles.labelGrid}>
          {selectedBinsList.map(bin => (
            <div
              key={bin.id}
              style={{
                ...styles.labelCard,
                width: currentSize.width,
              }}
            >
              <img
                src={bin.qr_image}
                alt={`QR for ${bin.name}`}
                style={{
                  width: currentSize.qrSize,
                  height: currentSize.qrSize,
                }}
              />
              <div style={{ ...styles.labelText, fontSize: currentSize.fontSize }}>
                <div style={styles.binName}>{bin.name}</div>
                <div style={styles.formulaName}>{bin.formula_name}</div>
                {bin.price_per_lb && (
                  <div style={styles.price}>${bin.price_per_lb.toFixed(2)}/lb</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>

      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-area, .print-area * {
            visibility: visible;
          }
          .print-area {
            position: absolute;
            left: 0;
            top: 0;
          }
        }
      `}</style>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f0f0f0',
    display: 'flex',
    flexDirection: 'column',
  },
  printPage: {
    backgroundColor: '#fff',
  },
  main: {
    flex: 1,
    padding: '1.5rem',
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
  },
  printMain: {
    padding: '0.5rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: 'bold',
    color: '#1a4d1a',
    margin: 0,
  },
  backBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#666',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  controls: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    alignItems: 'center',
    backgroundColor: '#fff',
    padding: '1rem',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  controlGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  label: {
    fontWeight: '500',
    color: '#333',
    fontSize: '0.9rem',
  },
  select: {
    padding: '0.5rem',
    border: '2px solid #ddd',
    borderRadius: '6px',
    fontSize: '0.9rem',
  },
  selectAllBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#f5f5f5',
    border: '2px solid #ddd',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  printBtn: {
    padding: '0.5rem 1.5rem',
    backgroundColor: '#1a4d1a',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '600',
    marginLeft: 'auto',
  },
  loading: {
    textAlign: 'center',
    color: '#666',
    padding: '2rem',
  },
  binList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '0.75rem',
    marginBottom: '2rem',
  },
  binItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem',
    backgroundColor: '#fff',
    border: '2px solid #ddd',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  checkbox: {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
  },
  binInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  binDetail: {
    fontSize: '0.85rem',
    color: '#666',
  },
  labelGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1rem',
    justifyContent: 'center',
    padding: '1rem',
    backgroundColor: '#fff',
    borderRadius: '12px',
    minHeight: '200px',
  },
  labelGridPrint: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    justifyContent: 'flex-start',
    pageBreakInside: 'avoid',
  },
  labelCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '0.75rem',
    border: '1px solid #ddd',
    borderRadius: '8px',
    backgroundColor: '#fff',
    pageBreakInside: 'avoid',
  },
  labelText: {
    textAlign: 'center',
    marginTop: '0.5rem',
  },
  binName: {
    fontWeight: 'bold',
    color: '#333',
  },
  formulaName: {
    color: '#666',
    marginTop: '0.125rem',
  },
  price: {
    fontWeight: '600',
    color: '#1a4d1a',
    marginTop: '0.25rem',
  },
};

export default BYOCLabels;
